import React, { useState, useEffect } from 'react';
import { useSwipeToDelete } from '../hooks/useSwipeToDelete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBell, faChevronDown, faTrash } from '@fortawesome/free-solid-svg-icons';
function relativeTime(iso) {
    const diffMs = Date.now() - new Date(iso).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60)
        return diffMins <= 1 ? 'just now' : `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
        return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
}
const PBSummary = ({ pbs }) => {
    const hasRankings = pbs.some(pb => pb.countyRanking || pb.regionRanking || pb.clubRanking || pb.nationalRanking);
    return (React.createElement("div", { className: "table-container" },
        React.createElement("table", { style: { width: '100%' } },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, "Event"),
                    React.createElement("th", { className: "hide-on-mobile" }, "Previous PB"),
                    React.createElement("th", { className: "hide-on-mobile" }, "New PB"),
                    React.createElement("th", { className: "hide-on-mobile" }, "Improvement"),
                    hasRankings && React.createElement("th", { className: "hide-on-mobile" }, "Rankings"))),
            React.createElement("tbody", null, pbs.map((pb, i) => {
                const rankingMeta = [
                    pb.countyRanking ? `County #${pb.countyRanking}${pb.countyTotal ? `/${pb.countyTotal}` : ''}` : null,
                    pb.regionRanking ? `Region #${pb.regionRanking}${pb.regionTotal ? `/${pb.regionTotal}` : ''}` : null,
                    pb.clubRanking ? `Club #${pb.clubRanking}${pb.clubTotal ? `/${pb.clubTotal}` : ''}` : null,
                    pb.nationalRanking ? `National #${pb.nationalRanking}${pb.nationalTotal ? `/${pb.nationalTotal}` : ''}` : null,
                ].filter(Boolean).join(' · ');
                return (React.createElement("tr", { key: i },
                    React.createElement("td", { "data-label": "Event", className: "card-event-td" },
                        React.createElement("div", { className: "card-event-header" },
                            React.createElement("div", null,
                                pb.event,
                                "\u00A0",
                                React.createElement("span", { className: "swimmer-tag-badge" }, pb.course === '50m' ? 'LC' : 'SC')),
                            React.createElement("span", { className: "mobile-supplement card-inline-time faster" }, pb.newTime)),
                        React.createElement("div", { className: "mobile-supplement card-meta-row" },
                            pb.previousBest ? `Was: ${pb.previousBest}` : 'New event',
                            pb.improvement ? ` · -${pb.improvement}` : ''),
                        rankingMeta && (React.createElement("div", { className: "mobile-supplement card-meta-row" }, rankingMeta))),
                    React.createElement("td", { "data-label": "Previous PB", className: "hide-on-mobile", style: { color: 'var(--text-secondary)' } }, pb.previousBest ?? '-'),
                    React.createElement("td", { "data-label": "New PB", className: "hide-on-mobile" }, pb.newTime),
                    React.createElement("td", { "data-label": "Improvement", className: "hide-on-mobile" }, pb.improvement ? `-${pb.improvement}` : '-'),
                    hasRankings && (React.createElement("td", { "data-label": "Rankings", className: "hide-on-mobile", style: { fontSize: '0.85rem' } },
                        pb.countyRanking
                            ? React.createElement("div", null,
                                "County: ",
                                React.createElement("strong", null,
                                    "#",
                                    pb.countyRanking),
                                pb.countyTotal ? React.createElement("span", { style: { color: 'var(--text-secondary)' } },
                                    " of ",
                                    pb.countyTotal) : '')
                            : null,
                        pb.regionRanking
                            ? React.createElement("div", null,
                                "Region: ",
                                React.createElement("strong", null,
                                    "#",
                                    pb.regionRanking),
                                pb.regionTotal ? React.createElement("span", { style: { color: 'var(--text-secondary)' } },
                                    " of ",
                                    pb.regionTotal) : '')
                            : null,
                        pb.clubRanking
                            ? React.createElement("div", null,
                                "Club: ",
                                React.createElement("strong", null,
                                    "#",
                                    pb.clubRanking),
                                pb.clubTotal ? React.createElement("span", { style: { color: 'var(--text-secondary)' } },
                                    " of ",
                                    pb.clubTotal) : '')
                            : null,
                        pb.nationalRanking
                            ? React.createElement("div", null,
                                "National: ",
                                React.createElement("strong", null,
                                    "#",
                                    pb.nationalRanking),
                                pb.nationalTotal ? React.createElement("span", { style: { color: 'var(--text-secondary)' } },
                                    " of ",
                                    pb.nationalTotal) : '')
                            : null,
                        !pb.countyRanking && !pb.nationalRanking ? '-' : null))));
            })))));
};
const SwipeableNotificationCard = ({ n, collapsed, onToggleCollapse, onCardClick, onDelete }) => {
    const { offset, isSwiping, swipeProgress, handlers } = useSwipeToDelete(() => onDelete(n.id));
    const handleDeleteClick = (e) => {
        e.stopPropagation();
        onDelete(n.id);
    };
    return (React.createElement("div", { style: { position: 'relative', borderRadius: '8px', overflow: 'hidden' } },
        React.createElement("div", { "aria-hidden": "true", style: {
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                minWidth: '80px',
                background: 'var(--danger)',
                opacity: swipeProgress,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: '1.25rem',
                gap: '0.4rem',
                color: 'white',
                fontSize: '0.85rem',
                transition: isSwiping ? 'none' : 'opacity 0.2s',
                pointerEvents: 'none',
            } },
            React.createElement(FontAwesomeIcon, { icon: faTrash }),
            swipeProgress > 0.6 && React.createElement("span", null, "Delete")),
        React.createElement("div", { className: "club-group", style: {
                borderLeft: n.read ? undefined : '2px solid var(--primary)',
                opacity: n.read ? 0.65 : undefined,
                background: 'linear-gradient(145deg, color-mix(in srgb, var(--primary) 7%, var(--bg-primary)) 0%, var(--bg-primary) 70%)',
                cursor: 'pointer',
                transform: `translateX(${offset}px)`,
                transition: isSwiping ? 'none' : 'transform 0.28s ease',
                touchAction: 'pan-y',
                borderRadius: '8px',
                overflow: 'hidden',
                userSelect: 'none',
            }, ...handlers, onClick: onCardClick },
            React.createElement("div", { className: "club-header", onClick: onToggleCollapse },
                React.createElement("h3", { className: "club-header-title", style: { fontWeight: n.read ? undefined : 700 } },
                    React.createElement(FontAwesomeIcon, { icon: faBell }),
                    "\u00A0",
                    n.swimmerName,
                    "\u00A0",
                    React.createElement("span", { className: "swimmer-tag-badge" },
                        n.pbs.length,
                        " PB",
                        n.pbs.length !== 1 ? 's' : '')),
                React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '0.75rem' } },
                    React.createElement("span", { style: { fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' } },
                        !n.read && (React.createElement("span", null,
                            React.createElement(FontAwesomeIcon, { icon: faBell, style: { color: 'var(--danger)' } }),
                            "\u00A0")),
                        relativeTime(n.timestamp)),
                    React.createElement("button", { className: "notification-delete-btn btn-ghost", onClick: handleDeleteClick, title: "Delete notification", "aria-label": "Delete notification" },
                        React.createElement(FontAwesomeIcon, { icon: faTrash })),
                    React.createElement(FontAwesomeIcon, { icon: faChevronDown, className: `chevron-icon${!collapsed ? ' expanded' : ''}` }))),
            !collapsed && React.createElement(PBSummary, { pbs: n.pbs }))));
};
const NotificationsPage = ({ onBack, onSwimmerClick, notifications, onMarkRead, onMarkAllRead, onClearAll, onDelete, loading }) => {
    const [collapsedNotifications, setCollapsedNotifications] = useState(() => new Set(notifications.filter(n => n.read).map(n => n.id)));
    useEffect(() => {
        setCollapsedNotifications(prev => {
            const next = new Set(prev);
            notifications.forEach(n => {
                if (n.read && !next.has(n.id))
                    next.add(n.id);
            });
            return next;
        });
    }, [notifications]);
    const hasUnread = notifications.some(n => !n.read);
    const toggleNotificationCollapse = (id, e) => {
        e.stopPropagation();
        setCollapsedNotifications(prev => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    const handleCardClick = (n) => {
        if (!n.read)
            onMarkRead(n.id);
        onSwimmerClick(n.tiref);
    };
    return (React.createElement("section", { className: "about-page card" },
        React.createElement("div", { className: "section-header" },
            React.createElement("button", { className: "btn-ghost section-toggle", onClick: onBack, title: "Back", "aria-label": "Back" },
                React.createElement(FontAwesomeIcon, { icon: faArrowLeft }),
                " Back"),
            React.createElement("h2", null, "Alerts"),
            React.createElement("div", { style: { marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' } },
                hasUnread && (React.createElement("button", { className: "btn-ghost", onClick: onMarkAllRead, style: { color: 'var(--primary)', fontSize: '0.85rem' } }, "Mark all read")),
                notifications.length > 0 && (React.createElement("button", { className: "btn-ghost", onClick: onClearAll, style: { color: 'var(--danger)', fontSize: '0.85rem' } }, "Clear all")))),
        loading && notifications.length === 0 ? (React.createElement("p", { style: { color: 'var(--text-secondary)', textAlign: 'center', marginTop: '2rem' } }, "Loading...")) : notifications.length === 0 ? (React.createElement("div", { style: { textAlign: 'center', marginTop: '3rem', color: 'var(--text-secondary)' } },
            React.createElement(FontAwesomeIcon, { icon: faBell, style: { fontSize: '2.5rem', marginBottom: '1rem', opacity: 0.3 } }),
            React.createElement("p", null, "No notifications yet"),
            React.createElement("p", { style: { fontSize: '0.85rem' } }, "Enable alerts on a saved swimmer to get notified when they post new personal bests."))) : (React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' } }, notifications.map(n => (React.createElement(SwipeableNotificationCard, { key: n.id, n: n, collapsed: collapsedNotifications.has(n.id), onToggleCollapse: (e) => toggleNotificationCollapse(n.id, e), onCardClick: () => handleCardClick(n), onDelete: onDelete })))))));
};
export default NotificationsPage;
